/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : VehicleSystemsAddDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 04-03-2025
 * @description : Component to add vehicle systems.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack } from "@mui/material";
import {
  HandleApiActions,
  adminSettingsAPI,
  labCar,
} from "../../redux/actions";
import { useDispatch, useSelector } from "react-redux";
import CustomDialogBoxV2 from "../customComponentsV2/common/customDialogBox/CustomDialogBoxV2";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomSelectV2 from "../customComponentsV2/common/select/CustomSelectV2";
import MultiselectConnectorV2 from "../customComponentsV2/common/connectors/MultiselectConnectorV2";
import UsersMultiselect from "./UsersMultiselect";
import CustomTabsV2 from "../customComponentsV2/common/tabs/CustomTabsV2";
import AttributesForm from "../../pages/labcar/Attributes/AttributesForm";

function VehicleSystemsAddDialog({
  openDialog,
  setOpenDialog,
  title,
  variantOptionList,
  selectedProjectDetails,
  selectedSingleVariantDetails,
  selectedZoneDetails,
  supplierOptionsList,
  vsOptionsList = [],
  getVehicleSystems,
  getZoneVSCountList,
  recreationMode,
  recreationParentVSList,
  selectedParentVSDetails,
  setSelectedParentVSDetails,
}) {
  /**
   * ===================== State Initialization =====================
   */
  /** ============== Redux ============== **/
  const dispatch = useDispatch();
  const adminSettingsDetails = useSelector(
    (state) => state.adminSettingsReducer,
  );
  /** ============== Add Dialog ============== **/
  const [selectedVehicleSystem, setSelectedVehicleSystem] = useState(null);
  const [assemblyNo, setAssemblyNo] = useState("");
  const [assemblyNoError, setAssemblyNoError] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionError, setDescriptionError] = useState("");
  const [selectedVariants, setSelectedVariants] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [attributeFormData, setAttributeFormData] = useState({
    added: [],
    edited: [],
    deleted: [],
  });
  const [vsAddTabValue, setVsAddTabValue] = useState(0);

  // recreate state
  const [coOwners, setCoOwners] = useState([]);
  const [editors, setEditors] = useState([]);
  const [viewers, setViewers] = useState([]);
  const [recreateZonePosition, setRecreateZonePosition] = useState(null);

  const normalizeSupplierOptions = (list) => {
    if (!Array.isArray(list)) return [];
    return list
      .map((opt) => {
        if (!opt) return null;
        if (typeof opt === "string") return { value: opt, label: opt };
        const value = opt.value ?? opt.id ?? opt.name ?? opt.label;
        const label = opt.label ?? opt.name ?? opt.value ?? opt.id;
        if (value == null || label == null) return null;
        return { value, label };
      })
      .filter(Boolean);
  };

  const suppliersFromProps = useMemo(
    () => normalizeSupplierOptions(supplierOptionsList),
    [supplierOptionsList],
  );

  const suppliersFromStore = useMemo(() => {
    const list = adminSettingsDetails?.adminSettings?.data?.ecu_suppliers;
    return normalizeSupplierOptions(list);
  }, [adminSettingsDetails]);

  const resolvedSupplierOptionsList =
    suppliersFromProps.length > 0 ? suppliersFromProps : suppliersFromStore;

  // Fetch admin settings when dialog opens if suppliers are not available yet.
  useEffect(() => {
    if (!openDialog) return;
    if (resolvedSupplierOptionsList.length > 0) return;

    dispatch(
      HandleApiActions({
        ...adminSettingsAPI.get_admin_settings,
        show_toast: false,
      }),
    );
  }, [openDialog, resolvedSupplierOptionsList.length, dispatch]);

  // Pre-select active variant when dialog opens, similar to EE Component logic
  useEffect(() => {
    if (openDialog && selectedSingleVariantDetails?.variant_id != null) {
      const id = selectedSingleVariantDetails.variant_id;
      setSelectedVariants((prev) => {
        const prevArr = Array.isArray(prev) ? prev : [];
        const hasId = prevArr.map(String).includes(String(id));
        if (hasId) return prevArr;
        return [id, ...prevArr];
      });
    }
    setVsAddTabValue(0);
  }, [openDialog, selectedSingleVariantDetails]);



  /**
   * ===================== API Call Functions =====================
   */

  const handleVSAttributeAdd = useCallback((data = {}) => {
    const added = Array.isArray(data?.added) ? data.added : [];
    const edited = Array.isArray(data?.edited) ? data.edited : [];
    const deleted = Array.isArray(data?.deleted) ? data.deleted : [];

    setAttributeFormData((prev) => {
      const nextData = { added, edited, deleted };
      return JSON.stringify(prev) === JSON.stringify(nextData)
        ? prev
        : nextData;
    });
  }, []);

  const addedAttributeFormRows = useMemo(
    () =>
      (Array.isArray(attributeFormData?.added)
        ? attributeFormData.added
        : []
      ).map((attribute) => ({
        ...attribute,
        attribute_id: null,
        newlyAdded: true,
        edited: false,
      })),
    [attributeFormData?.added],
  );
  /**
   * @author : Narender
   * @Date : 03-03-2025
   * @description : Adds a vehicle system
   * @param {project_id, variant_id, name, assembly_number, description, suppliers}
   * @return {msg: "", data: [], error: true | false}
   */
  const addVehicleSystem = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const isZonal = selectedProjectDetails?.is_zonal;
    const zonePosition = selectedZoneDetails?.position;
    // Call API only if required data is present
    if (!projectId || !variantId || (isZonal && !zonePosition)) return;
    const apiDetails = labCar.add_vehicle_system;
    handleCloseDialog();

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: true,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          ...(isZonal && { zone_position: zonePosition }), // Add zone position if zonal
          name: selectedVehicleSystem,
          assembly_number: assemblyNo?.trim(),
          description: description,
          suppliers: selectedSuppliers,
          ...(selectedVariants?.length
            ? { variant_ids: selectedVariants }
            : {}),
          attributes: attributeFormData.added, // Pass attribute data from form
        },
      }),
    );
    if (!resp.error) {
      getVehicleSystems();
      getZoneVSCountList();
    }
  };

  const handleRecreationAddClick = async () => {
    const projectId = selectedProjectDetails?.project_id;
    const variantId = selectedSingleVariantDetails?.variant_id;
    const isZonal = selectedProjectDetails?.is_zonal;
    const zonePosition = selectedZoneDetails?.position;
    // Call API only if required data is present
    if (!projectId || !variantId || (isZonal && !zonePosition)) return;
    const apiDetails = labCar.recreate_vs;
    handleCloseDialog();

    const resp = await dispatch(
      HandleApiActions({
        ...apiDetails,
        show_toast: true,
        params: {
          project_id: Number(projectId),
          variant_id: Number(variantId),
          ...(isZonal && { zone_position: recreateZonePosition }), // Add zone position if zonal
          name: selectedVehicleSystem,
          assembly_number: assemblyNo?.trim(),
          description: description,
          suppliers: selectedSuppliers,
          ...(selectedVariants?.length
            ? { variant_ids: selectedVariants }
            : {}),
          co_owners: coOwners?.map((data) => data?.value),
          edit_users: editors?.map((data) => data?.value),
          view_users: viewers?.map((data) => data?.value),
          parent_project_id: selectedProjectDetails?.parent_project_id,
          parent_vs_id: selectedParentVSDetails?.id,
        },
      }),
    );
    if (!resp.error) {
      getVehicleSystems();
      getZoneVSCountList();
    }
  };

  /**
   * ===================== UI & State Management Functions =====================
   */
  // Validates the fields in the dialog
  const isDialogFieldsValid = () => {
    const regexMin3 = /^[a-zA-Z0-9._-]{3,}$/;
    return (
      assemblyNo?.trim() !== "" &&
      !assemblyNoError &&
      regexMin3.test(assemblyNo.trim()) &&
      selectedVehicleSystem?.trim()?.length > 0 &&
      description?.trim() !== "" &&
      !descriptionError &&
      selectedSuppliers?.length > 0 &&
      !(
        recreationMode &&
        selectedProjectDetails?.is_zonal &&
        recreateZonePosition == null
      )
    );
  };
  // Clears all input fields in the dialog
  const clearDialogFields = () => {
    setAssemblyNo("");
    setAssemblyNoError("");
    setSelectedVehicleSystem(null);
    setDescription("");
    setDescriptionError("");
    setSelectedVariants([]);
    setSelectedSuppliers([]);
    setAttributeFormData({ added: [], edited: [], deleted: [] });
  };
  // Handles closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    clearDialogFields();
  };

  // recreate functions

  const setFormStates = (params) => {
    setAssemblyNo(params?.assembly_number);
    setSelectedVehicleSystem(params?.name);
    setDescription(params?.description);
    setSelectedSuppliers(params?.suppliers);

    // setSelectedVariants([params?.variant_id]);
    setRecreateZonePosition((prev) => {
      if (selectedProjectDetails?.is_zonal) {
        return params?.zone_details?.[0].position;
      } else {
        return null;
      }
    });
  };

  // recreate useEffects
  useEffect(() => {
    if (selectedParentVSDetails) {
      setFormStates(selectedParentVSDetails);
    }
  }, [selectedParentVSDetails]);

  return (
    <CustomDialogBoxV2
      open={openDialog}
      onClose={handleCloseDialog}
      title={title}
      dialogActions={
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            width: "100%",
          }}
        >
          <Box>
            {vsAddTabValue === 1 && !recreationMode && (
              <CustomButtonV2
                type="cancel"
                size="small"
                btnName={"Back"}
                handleOnClick={() => {
                  setVsAddTabValue(0);
                }}
              />
            )}
          </Box>
          <CustomButtonV2
            type="submit"
            size="small"
            disabled={!isDialogFieldsValid()}
            btnName={vsAddTabValue === 0 ? "Next" : "Add"}
            handleOnClick={() => {
              if (recreationMode) {
                handleRecreationAddClick();
              } else {
                if (vsAddTabValue === 0) {
                  setVsAddTabValue(1);
                } else {
                  addVehicleSystem();
                }
              }
            }}
          />
        </Box>
      }
    >
      <CustomTabsV2
        defaultTab={vsAddTabValue}
        value={vsAddTabValue}
        onTabChange={(newValue) => setVsAddTabValue(newValue)}
        tabs={[
          {
            name: "Details",
            content: (
              <Stack spacing={2} mb={2} sx={{ width: "450px" }}>
                {recreationMode && (
                  <CustomSelectV2
                    size={"small"}
                    label={"Select Parent VS"}
                    required={true}
                    optionsList={recreationParentVSList?.map((item) => {
                      return {
                        label: item?.assembly_number,
                        value: item,
                      };
                    })}
                    selectedValue={selectedParentVSDetails || " "}
                    handleOnChange={(e) =>
                      setSelectedParentVSDetails(e.target.value)
                    }
                  />
                )}
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Assembly No"}
                  required={true}
                  type={"text"}
                  fldType={"textR"}
                  value={assemblyNo}
                  setValue={(value) =>
                    value.length <= 40 && setAssemblyNo(value)
                  }
                  maxLength={40}
                />
                <CustomSelectV2
                  size={"small"}
                  label={"Select Vehicle System"}
                  optionsList={vsOptionsList}
                  selectedValue={selectedVehicleSystem || " "}
                  handleOnChange={(e) =>
                    setSelectedVehicleSystem(e.target.value)
                  }
                />
                <CustomFormTextFieldV2
                  size={"small"}
                  label={"Description"}
                  required={true}
                  value={description}
                  setValue={(value) =>
                    value.length <= 500 && setDescription(value)
                  }
                  maxLength={500}
                />
                {selectedProjectDetails?.is_zonal && recreationMode && (
                  <CustomSelectV2
                    placeholder={"Select Zone"}
                    label="Zone"
                    required={true}
                    selectedValue={recreateZonePosition}
                    handleOnChange={(e) => {
                      setRecreateZonePosition(e.target.value);
                    }}
                    optionsList={selectedProjectDetails?.project_zones?.map(
                      (zone) => ({
                        value: zone.position,
                        label: zone.zone_name,
                      }),
                    )}
                  />
                )}
                <MultiselectConnectorV2
                  required
                  mutiple
                  size={"small"}
                  label={"Select Suppliers"}
                  optionsList={resolvedSupplierOptionsList?.map((item) => ({
                    value: item?.value.toUpperCase(),
                    label: item?.label,
                  }))}
                  selectedValues={selectedSuppliers || " "}
                  setSelectedValues={setSelectedSuppliers}
                  placeholder={false}
                />

                <MultiselectConnectorV2
                  mutiple
                  selectedValues={selectedVariants || " "}
                  setSelectedValues={setSelectedVariants}
                  label={"Select Variants"}
                  optionsList={variantOptionList}
                  placeholder={false}
                  immutableValues={
                    selectedSingleVariantDetails?.variant_id != null
                      ? [selectedSingleVariantDetails?.variant_id]
                      : []
                  }
                />
                {recreationMode && (
                  <UsersMultiselect
                    coOwnerDetails={
                      selectedParentVSDetails?.co_owner_details || []
                    }
                    editorDetails={
                      selectedParentVSDetails?.edit_user_details || []
                    }
                    viewerDetails={
                      selectedParentVSDetails?.view_user_details || []
                    }
                    coOwners={coOwners}
                    setCoOwners={setCoOwners}
                    editors={editors}
                    setEditors={setEditors}
                    viewers={viewers}
                    setViewers={setViewers}
                    owner={
                      selectedParentVSDetails?.owner_details?.[0].user_id
                        ? [selectedParentVSDetails?.owner_details?.[0].user_id]
                        : []
                    }
                  />
                )}
              </Stack>
            ),
          },
          {
            name: "Attribute",
            content: (
              <Stack
                spacing={2}
                mb={2}
                justifyContent={"center"}
                sx={{ width: "450px" }}
              >
                <AttributesForm
                  type={"EDIT"}
                  attributeFromApi={addedAttributeFormRows}
                  getFormData={handleVSAttributeAdd}
                />
              </Stack>
            ),
          },
        ]}
      />
    </CustomDialogBoxV2>
  );
}

export default VehicleSystemsAddDialog;
